import React, { useState, useEffect, useCallback } from 'react';
import { useBackups } from '../../hooks/useApi';
import { usePermissions } from '../../hooks/usePermissions';
import { toastService } from '../../services/toastService';
import { CronExpressionParser } from 'cron-parser';
import Convert from 'ansi-to-html';

import PageHeader from '../common/PageHeader/PageHeader';
import Table from '../common/Table/Table';
import Button from '../common/Button/Button';
import Input from '../common/Input/Input';
import Select from '../common/Select/Select';
import PortalModal from '../common/PortalModal/PortalModal';
import './BackupManagement.css';

const convert = new Convert();

export default function BackupManagement() {
  const { getAllBackups, getFullBackups, getIncrementalBackups, getBackupInfo, getFullBackupSchedule, getIncrementalBackupSchedule, createBackup, restoreBackup, deleteBackup, loading } = useBackups();
  const { hasPermission } = usePermissions();

  // State
  const [backups, setBackups] = useState([]);
  const [fullSchedule, setFullSchedule] = useState(null);
  const [incrSchedule, setIncrSchedule] = useState(null);
  const [filterDate, setFilterDate] = useState({ start: '', end: '' });
  const [filterType, setFilterType] = useState('all');
  const [triggerRefresh, setTriggerRefresh] = useState(0);
  
  // Modal State
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [backupDetails, setBackupDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'delete' | 'restore', backup: object }
  const [confirmInput, setConfirmInput] = useState('');

  // --- Helpers ---
  const formatNextRun = (cronStr) => {
    if (!cronStr) return "Not Scheduled";
    try {
      const interval = CronExpressionParser.parse(cronStr);
      return interval.next().toDate().toLocaleString();
    } catch { return "Invalid Schedule"; }
  };

  const formatSize = (bytes) => (bytes / 1024 / 1024).toFixed(2) + ' MB';

  // --- Data Fetching ---
  const loadData = useCallback(async () => {
    try {
      let data = {};
      if (filterType === 'all') data = await getAllBackups();
      else if (filterType === 'full') data = await getFullBackups();
      else if (filterType === 'incremental') data = await getIncrementalBackups();
      
      let fetchedBackups = data.backups || [];
      
      // Date Filter
      if (filterDate.start) {
        fetchedBackups = fetchedBackups.filter(b => new Date(b.created_at) >= new Date(filterDate.start));
      }
      if (filterDate.end) {
        const endDate = new Date(filterDate.end);
        endDate.setHours(23, 59, 59);
        fetchedBackups = fetchedBackups.filter(b => new Date(b.created_at) <= endDate);
      }

      setBackups(fetchedBackups.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));

      // Schedules
      const fSched = await getFullBackupSchedule();
      if (fSched.schedule) setFullSchedule(fSched.schedule.slice(1, -1));
      
      const iSched = await getIncrementalBackupSchedule();
      if (iSched.schedule) setIncrSchedule(iSched.schedule.slice(1, -1));

    } catch (err) {
      console.error(err);
      toastService.error("Failed to load backups");
    }
  }, [filterType, filterDate, getAllBackups, getFullBackups, getIncrementalBackups, getFullBackupSchedule, getIncrementalBackupSchedule]);

  useEffect(() => {
    loadData();
  }, [triggerRefresh, loadData]);

  // --- Handlers ---
  const handleCreate = async (type) => {
    if (type === 'incremental') {
      const hasFull = backups.some(b => b.type === 'full');
      if (!hasFull) return toastService.error("Need a full backup first!");
    }
    toastService.info(`Starting ${type} backup...`);
    try {
      await createBackup(type);
      setTimeout(() => setTriggerRefresh(prev => prev + 1), 2000);
      toastService.success("Backup created!");
    } catch (e) {
      toastService.error(e.message);
    }
  };

  const handleViewDetails = async (backup) => {
    setSelectedBackup(backup);
    setDetailsLoading(true);
    try {
      const info = await getBackupInfo(backup.name, backup.type);
      setBackupDetails(info);
    } catch (e) {
      toastService.error("Failed to load details");
    } finally {
      setDetailsLoading(false);
    }
  };

  const executeAction = async () => {
    if (!confirmAction) return;
    const { type, backup } = confirmAction;
    
    try {
      if (type === 'delete') {
        await deleteBackup(backup.name, backup.type);
        toastService.success("Backup deleted");
      } else {
        await restoreBackup(backup.name, backup.type);
        toastService.success("Restore initiated");
      }
      setTriggerRefresh(prev => prev + 1);
      setConfirmAction(null);
      setConfirmInput('');
    } catch (e) {
      toastService.error(e.message);
    }
  };

  // --- Table Columns ---
  const columns = [
    {
      header: 'Timestamp',
      accessor: 'created_at',
      width: '180px',
      render: (row) => (
        <div className="timestamp-cell">
          <span className="timestamp-date">{new Date(row.created_at).toLocaleDateString()}</span>
          <span className="timestamp-time">{new Date(row.created_at).toLocaleTimeString()}</span>
        </div>
      )
    },
    {
      header: 'Type',
      accessor: 'type',
      width: '140px',
      render: (row) => (
        <span className={`backup-type-badge ${row.type}`}>
          {row.type === 'full' ? 'Full Backup' : 'Incremental'}
        </span>
      )
    },
    { 
      header: 'Instance', 
      accessor: 'instance',
      width: '200px'
    },
    { 
      header: 'Size', 
      accessor: 'size_bytes',
      width: '120px',
      render: (row) => formatSize(row.size_bytes) 
    },
    {
      header: 'Actions',
      width: '180px',
      render: (row) => (
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <Button variant="ghost" size="sm" icon="fas fa-eye" onClick={() => handleViewDetails(row)} />
          <Button 
            variant="ghost" 
            size="sm" 
            icon="fas fa-undo" 
            disabled={!hasPermission('backups', 'restore')}
            onClick={() => setConfirmAction({ type: 'restore', backup: row })} 
            title="Restore"
          />
          <Button 
            variant="danger" 
            size="sm" 
            icon="fas fa-trash" 
            disabled={!hasPermission('backups', 'delete')}
            onClick={() => setConfirmAction({ type: 'delete', backup: row })} 
          />
        </div>
      )
    }
  ];

  return (
    <div className="page-container backup-management-view">
      <div className="page-content">
        <PageHeader 
          title="Backup Management" 
          description="Manage system snapshots, schedule backups, and perform restores."
          icon="fas fa-floppy-disk" 
          accentColor="red" 
        />

        {/* Info Cards */}
        <div className="backup-info-grid">
          <div className="backup-info-card">
            <div className="backup-info-icon"><i className="fas fa-calendar-check"></i></div>
            <div className="backup-info-content">
              <div className="backup-info-label">Next Full Backup</div>
              <div className="backup-info-value">{formatNextRun(fullSchedule)}</div>
              <div className="backup-info-sub">Schedule: {fullSchedule || 'Disabled'}</div>
            </div>
          </div>
          <div className="backup-info-card">
            <div className="backup-info-icon"><i className="fas fa-clock"></i></div>
            <div className="backup-info-content">
              <div className="backup-info-label">Next Incremental</div>
              <div className="backup-info-value">{formatNextRun(incrSchedule)}</div>
              <div className="backup-info-sub">Schedule: {incrSchedule || 'Disabled'}</div>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="backup-toolbar">
          <div style={{ width: '160px' }}>
            <Input 
              type="date" 
              label="From" 
              value={filterDate.start} 
              onChange={(e) => setFilterDate({...filterDate, start: e.target.value})}
              onClick={(e) => e.target.showPicker && e.target.showPicker()}
            />
          </div>
          <div style={{ width: '160px' }}>
            <Input 
              type="date" 
              label="To" 
              value={filterDate.end} 
              onChange={(e) => setFilterDate({...filterDate, end: e.target.value})}
              onClick={(e) => e.target.showPicker && e.target.showPicker()}
            />
          </div>
          
          <div style={{ width: '200px' }}>
            <Select
              label="Backup Type"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              options={[
                { value: 'all', label: 'All Types' },
                { value: 'full', label: 'Full Backups' },
                { value: 'incremental', label: 'Incremental' }
              ]}
            />
          </div>

          <div className="toolbar-actions" style={{ flexDirection: 'column', alignItems: 'flex-end', gap: '0.75rem' }}>
            <Button 
              variant="secondary" 
              size="sm"
              icon="fas fa-sync" 
              onClick={() => setTriggerRefresh(prev => prev + 1)}
            >
              Refresh
            </Button>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <Button 
                variant="primary" 
                icon="fas fa-plus" 
                onClick={() => handleCreate('full')}
                disabled={!hasPermission('backups', 'create')}
              >
                Full Backup
              </Button>
              <Button 
                variant="primary" 
                icon="fas fa-plus" 
                onClick={() => handleCreate('incremental')}
                disabled={!hasPermission('backups', 'create')}
              >
                Incremental
              </Button>
            </div>
          </div>
        </div>

        {/* Table */}
        <Table 
          columns={columns} 
          data={backups} 
          isLoading={loading} 
          emptyMessage="No backups found matching criteria."
          emptyIcon="fas fa-hdd"
        />

        {/* Details Modal */}
        <PortalModal
          isOpen={!!selectedBackup && !confirmAction}
          onClose={() => { setSelectedBackup(null); setBackupDetails(null); }}
          title="Backup Details"
          size="large"
          footer={<Button variant="secondary" onClick={() => setSelectedBackup(null)}>Close</Button>}
        >
          {detailsLoading || !backupDetails ? (
            <div className="loading-state"><i className="fas fa-spinner fa-spin"></i> Loading...</div>
          ) : (
            <div className="backup-details-content">
              <div className="backup-details-grid">
                <div className="detail-block">
                  <h4>Information</h4>
                  <div className="detail-row"><span className="detail-label">Name</span><span className="detail-value">{backupDetails.backup.name}</span></div>
                  <div className="detail-row"><span className="detail-label">Type</span><span className="detail-value">{backupDetails.backup.type}</span></div>
                  <div className="detail-row"><span className="detail-label">Size</span><span className="detail-value">{formatSize(backupDetails.backup.size_bytes)}</span></div>
                </div>
                <div className="detail-block">
                  <h4>Metadata</h4>
                  <div className="metadata-box">{backupDetails.backup.metadata || 'No metadata'}</div>
                </div>
              </div>
              <div className="detail-block">
                <h4>Log Output</h4>
                <div className="log-container" dangerouslySetInnerHTML={{ __html: convert.toHtml(backupDetails.backup.log || '') }} />
              </div>
            </div>
          )}
        </PortalModal>

        {/* Confirm Action Modal */}
        <PortalModal
          isOpen={!!confirmAction}
          onClose={() => { setConfirmAction(null); setConfirmInput(''); }}
          title={`Confirm ${confirmAction?.type === 'delete' ? 'Delete' : 'Restore'}`}
          size="small"
          footer={
            <>
              <Button variant="ghost" onClick={() => setConfirmAction(null)}>Cancel</Button>
              <Button 
                variant="danger" 
                disabled={confirmInput !== confirmAction?.backup.name}
                onClick={executeAction}
              >
                Confirm {confirmAction?.type === 'delete' ? 'Delete' : 'Restore'}
              </Button>
            </>
          }
        >
          <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
            Are you sure you want to <strong>{confirmAction?.type}</strong> the backup <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{confirmAction?.backup.name}</span>?
            {confirmAction?.type === 'restore' && <span style={{ display: 'block', marginTop: '0.5rem', color: '#ef4444' }}>Warning: This will overwrite current system data.</span>}
          </p>
          
          <div className="confirm-input-wrapper">
            <div className="confirm-instruction">Type the backup name to confirm:</div>
            <Input 
              value={confirmInput} 
              onChange={(e) => setConfirmInput(e.target.value)} 
              placeholder={confirmAction?.backup.name}
              style={{ textAlign: 'center', fontWeight: 'bold' }}
            />
          </div>
        </PortalModal>

      </div>
    </div>
  );
}